import React, { useCallback, useEffect, useMemo } from "react";
import { ButtonGhostCompact, colors2, TextInput } from "oolib";
import { UI_CAPTION } from "oolib/dist/v2/components/Typo2";

import { ConfigForm_SectionWrapper } from "../../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { WidgetFilters } from "../../../utils/WidgetFilters";
import { useWidgetFilterControls } from "../../../utils/useWidgetFilterControls";
import { getWidgetContentTypes } from "../../../utils/getWidgetContentTypes";
import { BarChartConfigProvider } from "../../../WidgetBarChart/ConfigForm/ConfigForm_Context";
import { HeatMapDataSection } from "../../../WidgetHeatMapGrid/ConfigForm/ConfigForm_DataSection";
import { TEMP_WIDGET_PROPS_KEY } from "../../DATA_KEYS";
import { compileExpression } from "./expression";
import { SegmentedControl } from "@/components/UI/SegmentedControl";
import { DEFAULTS, MAX_TERMS, TERM_KEYS, VALUE_MODE_OPTIONS } from "./settings";

/**
 * Axes first — the heat map's OWN data section (cascade field picker, the
 * per-dimension show/hide values modifier, number-field bucketing, swap), not
 * a copy of it — then one section per term.
 *
 * A term is a filter set — built with the same WidgetFilters every
 * other widget's config uses, so any filter the platform supports can define a
 * scope, not just "field equals value". Each term therefore needs its own
 * filter controls (the "+" in its section header and the lightbox behind it),
 * which is why the controls hook is called once per SLOT: hooks cannot be
 * called in a loop, so all MAX_TERMS exist and the unused ones idle.
 *
 * The expression below names the terms by letter and is checked as it is typed.
 */
const sliceOf = (widget) => ({ ...widget, props: widget?.props?.[TEMP_WIDGET_PROPS_KEY] || {} });

export const CompareHeatmapConfigForm = ({ props, updateProps, propsPath, widgetConfig, setConfig, underEditWidgetIdx }) => {
  // The terms array has to EXIST in the saved config before the filter builder
  // writes into `terms.<i>.filters` — the container keeps no default props of
  // its own, so until something writes it there is nothing to index into.
  useEffect(() => {
    if (Array.isArray(props.terms)) return;
    updateProps((p) => ({ ...p, terms: DEFAULTS.terms, expression: p.expression ?? DEFAULTS.expression }));
  }, [props.terms, updateProps]);

  const terms = Array.isArray(props.terms) && props.terms.length ? props.terms : DEFAULTS.terms;
  const expression = props.expression ?? DEFAULTS.expression;
  const termKeys = terms.map((_, i) => TERM_KEYS[i]);
  const valueMode = props.valueMode ?? DEFAULTS.valueMode;
  const { error: parseError, baseline } = compileExpression(expression, termKeys);
  // Percent change is taken against what the expression subtracts; one that
  // subtracts nothing at its outermost level has nothing to be a percent of.
  const expressionError =
    parseError ||
    (valueMode === "percent" && !baseline
      ? "Percent change needs the expression to end in a subtraction, e.g. B - A — the part being subtracted is what the change is a percent of."
      : null);

  // A heat map CAN carry every sub-topic as an axis, so this widget opts in to
  // the "All sub-topics" option the bar and pie leave out.
  // The heat map's data section and the BarChart config context behind it read
  // and write `props.dimension1` etc. on the widget at underEditWidgetIdx. A temp
  // widget keeps its props one level down, under props.tempWidgetProps, so the
  // context gets the widget with that slice lifted to `props`, and a setConfig
  // that runs every update against the lifted view and folds the result back
  // into the slice — the context never learns it is nested.
  const scopedWidgetConfig = useMemo(() => sliceOf(widgetConfig), [widgetConfig]);
  const scopedSetConfig = useCallback(
    (updater) =>
      setConfig((prev) => {
        const lifted = prev.map((w, i) => (i === underEditWidgetIdx ? sliceOf(w) : w));
        const next = typeof updater === "function" ? updater(lifted) : updater;
        const real = prev[underEditWidgetIdx];
        const full = [...prev];
        full[underEditWidgetIdx] = {
          ...real,
          props: { ...real.props, [TEMP_WIDGET_PROPS_KEY]: next[underEditWidgetIdx]?.props || {} },
        };
        return full;
      }),
    [setConfig, underEditWidgetIdx]
  );

  // The same resolution every other widget's filters use: an annotation
  // dimension opens every annotation-enabled template, a document field opens
  // its own. Read off the TEMP widget's slice, which is shaped like a widget.
  const contentTypesForFilters = useMemo(
    () => getWidgetContentTypes({ props }, "WidgetHeatMapGrid"),
    [props]
  );
  const hasDimensions = !!props.dimension1 && !!props.dimension2;

  // One set of filter controls per slot — see the note above.
  const filterControls = TERM_KEYS.map(() =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useWidgetFilterControls({ hasDimensions })
  );
  const scopeControls = useWidgetFilterControls({ hasDimensions });

  const setTerms = (next) => updateProps((p) => ({ ...p, terms: next }));

  return (
    <>
      <ConfigForm_SectionWrapper icon="Database" title="Data">
        <BarChartConfigProvider
          thisWidgetConfig={scopedWidgetConfig}
          setConfig={scopedSetConfig}
          underEditWidgetIdx={underEditWidgetIdx}
          showNumberInputsInOptions={true}
          showDimension2Input={true}
        >
          <HeatMapDataSection />
        </BarChartConfigProvider>
      </ConfigForm_SectionWrapper>

      {terms.map((term, i) => (
        <ConfigForm_SectionWrapper
          key={TERM_KEYS[i]}
          icon="Funnel"
          title={`Term ${TERM_KEYS[i]}`}
          {...filterControls[i].filterHeaderProps}
        >
          {/* An empty term renders no filter UI of its own — the only
              affordance is the "+" in this section's header — so say what a
              term is and where to click, rather than showing a blank. */}
          {!term.filters?.length && (
            <UI_CAPTION style={{ color: colors2.grey60, display: "block" }}>
              {hasDimensions
                ? `The grid, counted over this term's own scope. Use + above to filter it — otherwise term ${TERM_KEYS[i]} is everything.`
                : "Pick both axes above, then filters can be added here."}
            </UI_CAPTION>
          )}
          <WidgetFilters
            widgetConfig={widgetConfig}
            updateWidgetConfig={setConfig}
            widgetIndex={underEditWidgetIdx}
            configPath={`${propsPath}.terms.${i}.filters`}
            contentTypesForFilters={contentTypesForFilters}
            {...filterControls[i].filterControlProps}
          />
          {terms.length > 1 && (
            <ButtonGhostCompact
              S
              icon="Trash"
              value={`Remove term ${TERM_KEYS[i]}`}
              onClick={() => setTerms(terms.filter((_, idx) => idx !== i))}
            />
          )}
        </ConfigForm_SectionWrapper>
      ))}

      <ConfigForm_SectionWrapper
        icon="Function"
        title="Expression"
        headerRight={
          terms.length < MAX_TERMS ? (
            <ButtonGhostCompact
              S
              icon="Plus"
              value={`Term ${TERM_KEYS[terms.length]}`}
              onClick={() => setTerms([...terms, { filters: [] }])}
            />
          ) : null
        }
      >
        <SegmentedControl
          options={VALUE_MODE_OPTIONS}
          value={valueMode}
          onChange={(v) => updateProps((p) => ({ ...p, valueMode: v }))}
        />
        <TextInput
          id="expression"
          label="Expression"
          value={expression}
          onChange={(_id, v) => updateProps((p) => ({ ...p, expression: v }))}
          placeholder="A - B"
        />
        <UI_CAPTION style={{ color: expressionError ? colors2.red : colors2.grey60, display: "block" }}>
          {expressionError
            ? expressionError
            : valueMode === "percent"
            ? `Each cell shows the expression as a percent of what it subtracts — for B - A, (B - A) ÷ A × 100.`
            : `Terms ${termKeys.join(", ")}, numbers, + − × ÷ and brackets.`}
        </UI_CAPTION>
      </ConfigForm_SectionWrapper>

      {/* The widget's own filters narrow the scope every term starts from. */}
      <ConfigForm_SectionWrapper icon="Funnel" title="Filters" {...scopeControls.filterHeaderProps}>
        <WidgetFilters
          widgetConfig={widgetConfig}
          updateWidgetConfig={setConfig}
          widgetIndex={underEditWidgetIdx}
          configPath={`${propsPath}.widgetFilters`}
          contentTypesForFilters={contentTypesForFilters}
          {...scopeControls.filterControlProps}
        />
      </ConfigForm_SectionWrapper>
    </>
  );
};
